import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import {
  ScanLine, Upload, Camera, ChevronRight, ChevronLeft, CheckCircle2,
  AlertTriangle, Loader2, FileText, ClipboardList, CreditCard, Calendar, Users
} from 'lucide-react';
import { useAppMode } from '../../contexts/AppModeContext';

// Sample documents per task type that MAs can select to simulate a Lens scan
const SAMPLE_DOCS = {
  'Intake forms': [
    { id: 'intake-1', label: 'Patient Intake Form (PDF)', icon: FileText, desc: 'Standard new patient registration form with demographics', color: 'bg-slate-50 text-slate-600' },
    { id: 'intake-2', label: 'Medical History Questionnaire', icon: ClipboardList, desc: 'Pre-visit health history and current medications', color: 'bg-slate-50 text-slate-600' },
  ],
  'Vitals': [
    { id: 'vitals-1', label: 'Vitals Sheet (Photo)', icon: ClipboardList, desc: 'Handwritten or printed vitals — BP, HR, temp, weight, O2', color: 'bg-slate-50 text-slate-600' },
    { id: 'vitals-2', label: 'Device Export (CSV)', icon: FileText, desc: 'Automated export from vitals monitor or wearable device', color: 'bg-slate-50 text-slate-600' },
  ],
  'Prior visit note': [
    { id: 'prior-1', label: 'Previous Visit Note', icon: FileText, desc: 'Last encounter note from referring or primary provider', color: 'bg-slate-50 text-slate-600' },
    { id: 'prior-2', label: 'Consultation Report', icon: ClipboardList, desc: 'Specialist consultation summary or letter', color: 'bg-slate-50 text-slate-600' },
  ],
  'Lab results': [
    { id: 'lab-1', label: 'Lab Report (CBC / BMP)', icon: FileText, desc: 'Complete blood count and basic metabolic panel', color: 'bg-red-50 text-red-600' },
    { id: 'lab-2', label: 'HbA1c / Lipid Panel', icon: ClipboardList, desc: 'Diabetes and cholesterol screening results', color: 'bg-red-50 text-red-600' },
  ],
  'Insurance card': [
    { id: 'ins-1', label: 'Insurance Card (Front)', icon: CreditCard, desc: 'Front of insurance card — member ID, group, copay', color: 'bg-blue-50 text-blue-600' },
    { id: 'ins-2', label: 'Insurance Card (Back)', icon: CreditCard, desc: 'Back of card — claims address, phone, Rx info', color: 'bg-blue-50 text-blue-600' },
  ],
  'New patient forms': [
    { id: 'new-1', label: 'New Patient Registration', icon: Users, desc: 'Full registration with emergency contact, PCP info', color: 'bg-slate-50 text-slate-600' },
    { id: 'new-2', label: 'Consent & HIPAA Forms', icon: FileText, desc: 'Signed consent to treat and HIPAA privacy notice', color: 'bg-slate-50 text-slate-600' },
  ],
  'Referral doc': [
    { id: 'ref-1', label: 'Referral Letter', icon: FileText, desc: 'Incoming referral from another provider with clinical notes', color: 'bg-violet-50 text-violet-600' },
    { id: 'ref-2', label: 'Prior Authorization', icon: ClipboardList, desc: 'Insurance pre-auth for specialist visit or procedure', color: 'bg-violet-50 text-violet-600' },
  ],
  'Clinical docs': [
    { id: 'clin-1', label: 'Progress Note', icon: FileText, desc: 'Clinical progress note from external provider', color: 'bg-emerald-50 text-emerald-600' },
    { id: 'clin-2', label: 'Imaging Report', icon: ClipboardList, desc: 'X-ray, MRI, or CT scan report and findings', color: 'bg-emerald-50 text-emerald-600' },
  ],
  'Patient schedule': [
    { id: 'sched-1', label: 'Patient Appointment List', icon: Calendar, desc: 'Upcoming appointments from external system export', color: 'bg-amber-50 text-amber-600' },
    { id: 'sched-2', label: 'Treatment Plan / Orders', icon: ClipboardList, desc: 'Physician orders with follow-up schedule', color: 'bg-amber-50 text-amber-600' },
  ],
};

// Mock extraction results per sample document
const DEMO_RESULTS = {
  'intake-1': { type: 'Patient Intake Form', fields: [
    { name: 'Patient Name', value: 'John Smith', confidence: 99 },
    { name: 'DOB', value: '03/15/1965', confidence: 98 },
    { name: 'SSN (last 4)', value: '••••4589', confidence: 96 },
    { name: 'Address', value: '1234 Elm Street, Austin TX 78704', confidence: 94 },
    { name: 'Phone', value: '(512) 555-0142', confidence: 97 },
    { name: 'Emergency Contact', value: 'Jane Smith — (512) 555-0143', confidence: 93 },
  ]},
  'intake-2': { type: 'Medical History', fields: [
    { name: 'Allergies', value: 'Penicillin (Rash), Sulfa (Hives)', confidence: 97 },
    { name: 'Current Meds', value: 'Metformin 500mg BID, Lisinopril 10mg QD', confidence: 94 },
    { name: 'Past Surgeries', value: 'Appendectomy (2015), Knee arthroscopy (2020)', confidence: 91 },
    { name: 'Family Hx', value: 'Father — MI age 62; Mother — Type 2 DM', confidence: 89 },
  ]},
  'vitals-1': { type: 'Vitals Sheet', fields: [
    { name: 'Blood Pressure', value: '128/82 mmHg', confidence: 96 },
    { name: 'Heart Rate', value: '76 bpm', confidence: 98 },
    { name: 'Temperature', value: '98.6°F', confidence: 97 },
    { name: 'Weight', value: '182 lbs', confidence: 95 },
    { name: 'O2 Saturation', value: '98%', confidence: 99 },
    { name: 'Respiratory Rate', value: '16 breaths/min', confidence: 94 },
  ]},
  'vitals-2': { type: 'Device Export', fields: [
    { name: 'BP (avg 7-day)', value: '124/78 mmHg', confidence: 97 },
    { name: 'Resting HR', value: '72 bpm', confidence: 98 },
    { name: 'SpO2 (avg)', value: '97.8%', confidence: 96 },
    { name: 'Weight Trend', value: '180 → 182 lbs (+2 in 30d)', confidence: 93 },
  ]},
  'prior-1': { type: 'Visit Note', fields: [
    { name: 'Provider', value: 'Dr. Sarah Chen, MD', confidence: 99 },
    { name: 'Visit Date', value: '01/15/2026', confidence: 98 },
    { name: 'Chief Complaint', value: 'Diabetes management follow-up', confidence: 95 },
    { name: 'Assessment', value: 'E11.9 — A1c 6.8%, well controlled', confidence: 93 },
    { name: 'Plan', value: 'Continue current regimen, recheck 3 months', confidence: 92 },
  ]},
  'prior-2': { type: 'Consultation Report', fields: [
    { name: 'Specialist', value: 'Dr. Michael Park — Cardiology', confidence: 97 },
    { name: 'Reason', value: 'Cardiac clearance for DM management', confidence: 95 },
    { name: 'Findings', value: 'EF 60%, normal sinus rhythm, Grade 1 DD', confidence: 93 },
    { name: 'Recommendation', value: 'No cardiac limitations, annual echo', confidence: 91 },
  ]},
  'lab-1': { type: 'Lab Report', fields: [
    { name: 'WBC', value: '7.2 K/uL (Normal)', confidence: 97 },
    { name: 'Hemoglobin', value: '14.2 g/dL (Normal)', confidence: 96 },
    { name: 'Platelets', value: '245 K/uL (Normal)', confidence: 95 },
    { name: 'BUN', value: '18 mg/dL (Normal)', confidence: 94 },
    { name: 'Creatinine', value: '1.1 mg/dL (Normal)', confidence: 96 },
    { name: 'eGFR', value: '72 mL/min (Low)', confidence: 93 },
  ]},
  'lab-2': { type: 'HbA1c / Lipid Panel', fields: [
    { name: 'HbA1c', value: '7.2% (High — Target: <7.0%)', confidence: 97 },
    { name: 'Fasting Glucose', value: '142 mg/dL (High)', confidence: 96 },
    { name: 'Total Cholesterol', value: '212 mg/dL (Borderline)', confidence: 95 },
    { name: 'LDL', value: '128 mg/dL (High — Target: <100)', confidence: 94 },
    { name: 'HDL', value: '48 mg/dL (Low)', confidence: 93 },
    { name: 'Triglycerides', value: '180 mg/dL (Borderline)', confidence: 92 },
  ]},
  'ins-1': { type: 'Insurance Card (Front)', fields: [
    { name: 'Plan', value: 'Blue Cross Blue Shield PPO', confidence: 97 },
    { name: 'Member ID', value: 'BCB-123456789', confidence: 96 },
    { name: 'Group Number', value: 'GRP-78945', confidence: 94 },
    { name: 'Copay', value: '$25 PCP / $50 Specialist', confidence: 91 },
    { name: 'Effective Date', value: '01/01/2026', confidence: 95 },
  ]},
  'ins-2': { type: 'Insurance Card (Back)', fields: [
    { name: 'Claims Address', value: 'PO Box 45123, Jacksonville FL 32231', confidence: 93 },
    { name: 'Member Services', value: '1-800-555-BCBS', confidence: 96 },
    { name: 'Rx BIN', value: '004336', confidence: 94 },
    { name: 'Rx PCN', value: 'ADV', confidence: 92 },
    { name: 'Rx Group', value: 'RX9876', confidence: 91 },
  ]},
  'new-1': { type: 'New Patient Registration', fields: [
    { name: 'Patient', value: 'Maria Garcia', confidence: 99 },
    { name: 'DOB', value: '07/22/1988', confidence: 98 },
    { name: 'PCP', value: 'Dr. Sarah Chen', confidence: 95 },
    { name: 'Emergency Contact', value: 'Carlos Garcia — (512) 555-0199', confidence: 93 },
    { name: 'Preferred Pharmacy', value: 'CVS — 2100 S Lamar, Austin TX', confidence: 91 },
  ]},
  'new-2': { type: 'Consent & HIPAA', fields: [
    { name: 'Patient Signature', value: 'Detected — Maria Garcia', confidence: 96 },
    { name: 'Date Signed', value: '03/27/2026', confidence: 98 },
    { name: 'HIPAA Acknowledged', value: 'Yes', confidence: 99 },
    { name: 'Consent to Treat', value: 'Yes', confidence: 99 },
  ]},
  'ref-1': { type: 'Referral Letter', fields: [
    { name: 'Referring Provider', value: 'Dr. Patel — Internal Medicine', confidence: 97 },
    { name: 'Patient', value: 'Robert Johnson', confidence: 99 },
    { name: 'Reason', value: 'Persistent cough >4 weeks, rule out asthma/GERD', confidence: 94 },
    { name: 'Relevant Hx', value: 'Non-smoker, no prior pulmonary dx', confidence: 91 },
    { name: 'Requested', value: 'PFTs, chest CT if indicated', confidence: 90 },
  ]},
  'ref-2': { type: 'Prior Authorization', fields: [
    { name: 'Auth Number', value: 'PA-2026-78234', confidence: 97 },
    { name: 'Service', value: 'Specialist Consultation — Pulmonology', confidence: 95 },
    { name: 'Approved Visits', value: '3 visits through 06/30/2026', confidence: 94 },
    { name: 'Status', value: 'Approved', confidence: 99 },
  ]},
  'clin-1': { type: 'Progress Note', fields: [
    { name: 'Provider', value: 'Dr. Chen — Lumina', confidence: 98 },
    { name: 'Date', value: '03/15/2026', confidence: 97 },
    { name: 'Summary', value: 'Cardiology follow-up, stable on current regimen', confidence: 93 },
    { name: 'Assessment', value: 'I10 HTN — well controlled on Lisinopril 10mg', confidence: 92 },
  ]},
  'clin-2': { type: 'Imaging Report', fields: [
    { name: 'Study', value: 'Chest X-ray (PA & Lateral)', confidence: 97 },
    { name: 'Date', value: '03/20/2026', confidence: 98 },
    { name: 'Findings', value: 'Clear lung fields, no infiltrates, no effusion', confidence: 95 },
    { name: 'Impression', value: 'Normal chest radiograph', confidence: 96 },
  ]},
  'sched-1': { type: 'Appointment List', fields: [
    { name: 'Next Appointment', value: '04/15/2026 @ 10:00 AM', confidence: 97 },
    { name: 'Provider', value: 'Dr. Sarah Chen', confidence: 98 },
    { name: 'Type', value: 'Follow-up — Diabetes Management', confidence: 95 },
    { name: 'Frequency', value: 'Every 3 months', confidence: 92 },
  ]},
  'sched-2': { type: 'Treatment Plan', fields: [
    { name: 'Orders', value: 'HbA1c, Lipid Panel, CMP due 04/01/2026', confidence: 96 },
    { name: 'Follow-up', value: 'PCP in 3 months, Cardio annual', confidence: 94 },
    { name: 'Medications', value: 'Continue Metformin 500mg BID', confidence: 93 },
  ]},
};

const DEFAULT_SAMPLES = SAMPLE_DOCS['Intake forms'];

const DocumentUploadFlow = ({ open, onOpenChange, patientName = 'Patient', patients, mode = 'single', taskType = null, onComplete }) => {
  const { isDemo } = useAppMode();
  const [step, setStep] = useState(0); // 0=select, 1=scanning, 2=review, 3=done
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [processing, setProcessing] = useState(false);

  const samples = (taskType && SAMPLE_DOCS[taskType]) || DEFAULT_SAMPLES;

  const handleSelectDoc = (doc) => {
    setSelectedDoc(doc);
    setProcessing(true);
    setStep(1);
    // Simulate scanning
    setTimeout(() => {
      const result = DEMO_RESULTS[doc.id] || { type: 'Document', fields: [{ name: 'Data', value: 'Extracted', confidence: 95 }] };
      setScanResult(result);
      setProcessing(false);
      setStep(2);
    }, 2000);
  };

  const handleConfirm = () => {
    setStep(3);
    setTimeout(() => {
      onComplete?.([{
        name: selectedDoc?.label || 'Document',
        docType: scanResult?.type || taskType || 'Document',
        extractedFields: scanResult?.fields?.map(f => ({ name: f.name, value: f.value, confidence: f.confidence })) || [],
      }]);
      reset();
      onOpenChange(false);
    }, 600);
  };

  const reset = () => {
    setStep(0);
    setSelectedDoc(null);
    setScanResult(null);
    setProcessing(false);
  };

  const handleClose = () => { reset(); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden" style={{ maxHeight: '85vh' }} aria-describedby={undefined} data-testid="lens-upload-dialog">

        {/* Header */}
        <div className="flex items-center justify-between" style={{ padding: '14px 20px', borderBottom: '0.5px solid #e8e8e8' }}>
          <div>
            <DialogTitle style={{ fontSize: 16, fontWeight: 500, color: '#111111', margin: 0 }}>
              {step === 0 ? 'Scan with Lens' : step === 1 ? 'Scanning document...' : step === 2 ? 'Review extracted data' : 'Done'}
            </DialogTitle>
            {taskType && step === 0 && <p style={{ fontSize: 12, color: '#888888', marginTop: 2 }}>{taskType} · {patientName}</p>}
          </div>
          <span style={{
            fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20,
            background: '#f5f5f5', color: '#555555',
          }} data-testid="mode-indicator">{patientName}</span>
        </div>

        <div style={{ padding: '16px 20px', maxHeight: '60vh', overflowY: 'auto' }}>

          {/* ━━━ STEP 0: SELECT SAMPLE DOCUMENT ━━━ */}
          {step === 0 && (
            <div data-testid="lens-step-select">
              <p style={{ fontSize: 12, color: '#555555', marginBottom: 14 }}>
                Select a document to scan, or upload your own file. Lens will extract clinical data automatically.
              </p>

              {/* Sample documents */}
              <p style={{ fontSize: 10, color: '#aaaaaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Sample documents{taskType ? ` — ${taskType}` : ''}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                {samples.map(doc => {
                  const Icon = doc.icon;
                  return (
                    <button key={doc.id}
                      onClick={() => handleSelectDoc(doc)}
                      className="flex items-center text-left"
                      style={{ padding: '12px 14px', borderRadius: 10, border: '0.5px solid #e8e8e8', background: '#ffffff', cursor: 'pointer', gap: 12, transition: 'border-color 150ms, background 100ms' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#111111'; e.currentTarget.style.background = '#fafafa'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e8e8'; e.currentTarget.style.background = '#ffffff'; }}
                      data-testid={`sample-${doc.id}`}
                    >
                      <div className={`flex items-center justify-center flex-shrink-0 rounded-lg ${doc.color}`} style={{ width: 40, height: 40 }}>
                        <Icon style={{ width: 18, height: 18 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 13, fontWeight: 500, color: '#111111', margin: 0 }}>{doc.label}</p>
                        <p style={{ fontSize: 11, color: '#888888', margin: '2px 0 0' }}>{doc.desc}</p>
                      </div>
                      <ChevronRight style={{ width: 14, height: 14, color: '#cccccc', flexShrink: 0 }} />
                    </button>
                  );
                })}
              </div>

              {/* Upload zone */}
              <div style={{ borderTop: '0.5px solid #f0f0f0', paddingTop: 14 }}>
                <p style={{ fontSize: 10, color: '#aaaaaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Or upload your own</p>
                <div style={{ background: '#fafafa', border: '1.5px dashed #d1d5db', borderRadius: 10, padding: '20px 16px', textAlign: 'center' }}>
                  <ScanLine style={{ width: 22, height: 22, color: '#888888', margin: '0 auto 6px' }} />
                  <p style={{ fontSize: 12, color: '#555555', margin: '0 0 4px', fontWeight: 500 }}>Take a photo or upload a file</p>
                  <p style={{ fontSize: 11, color: '#aaaaaa', margin: '0 0 12px' }}>Photos, PDFs, CSVs, images</p>
                  <div className="flex items-center justify-center" style={{ gap: 6 }}>
                    <button style={{ background: '#111111', color: '#ffffff', borderRadius: 8, height: 32, padding: '0 14px', fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }} data-testid="take-photo-btn">
                      <Camera style={{ width: 13, height: 13 }} /> Take photo
                    </button>
                    <button style={{ background: '#ffffff', color: '#555555', borderRadius: 8, height: 32, padding: '0 14px', fontSize: 12, fontWeight: 500, border: '0.5px solid #d1d5db', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }} data-testid="upload-file-btn">
                      <Upload style={{ width: 13, height: 13 }} /> Upload file
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ━━━ STEP 1: SCANNING ━━━ */}
          {step === 1 && (
            <div style={{ textAlign: 'center', padding: '32px 0' }} data-testid="lens-step-scanning">
              <div style={{ width: 48, height: 48, margin: '0 auto 14px', position: 'relative' }}>
                <ScanLine style={{ width: 24, height: 24, color: '#111111', position: 'absolute', top: 12, left: 12 }} />
                <div style={{ width: 48, height: 48, borderRadius: '50%', border: '2px solid #111111', opacity: 0.15, animation: 'pulse 1.5s infinite' }} />
                <Loader2 style={{ width: 48, height: 48, position: 'absolute', top: 0, left: 0, color: '#111111', opacity: 0.3, animation: 'spin 2s linear infinite' }} />
              </div>
              <p style={{ fontSize: 15, fontWeight: 500, color: '#111111', margin: '0 0 4px' }}>Scanning document...</p>
              <p style={{ fontSize: 12, color: '#888888' }}>{selectedDoc?.label}</p>
              <p style={{ fontSize: 11, color: '#aaaaaa', marginTop: 8 }}>Lens is extracting clinical data</p>
            </div>
          )}

          {/* ━━━ STEP 2: REVIEW ━━━ */}
          {step === 2 && scanResult && (
            <div data-testid="lens-step-review">
              <div className="flex items-center" style={{ gap: 8, marginBottom: 14 }}>
                <CheckCircle2 style={{ width: 16, height: 16, color: '#22c55e' }} />
                <p style={{ fontSize: 13, fontWeight: 500, color: '#111111', margin: 0 }}>Extraction complete</p>
              </div>

              <div style={{ background: '#fafafa', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                <p style={{ fontSize: 12, color: '#555555', margin: 0 }}>Review the extracted data below. This will be added to <span style={{ fontWeight: 500, color: '#111111' }}>{patientName}</span>'s chart.</p>
              </div>

              <p style={{ fontSize: 10, color: '#aaaaaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{scanResult.type} — Extracted fields</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
                {scanResult.fields.map((field, i) => (
                  <div key={i} className="flex items-center justify-between" style={{ padding: '8px 10px', borderRadius: 6, background: '#ffffff', border: '0.5px solid #e8e8e8' }}>
                    <span style={{ fontSize: 11, color: '#888888', flexShrink: 0, minWidth: 100 }}>{field.name}</span>
                    <div className="flex items-center" style={{ gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 500, color: '#111111' }}>{field.value}</span>
                      <span style={{
                        fontSize: 9, padding: '1px 5px', borderRadius: 10, fontWeight: 500,
                        background: field.confidence >= 95 ? '#f0fdf4' : field.confidence >= 85 ? '#fffbeb' : '#fef2f2',
                        color: field.confidence >= 95 ? '#166534' : field.confidence >= 85 ? '#92400e' : '#991b1b',
                      }}>{field.confidence}%</span>
                    </div>
                  </div>
                ))}
              </div>

              <p style={{ fontSize: 10, color: '#aaaaaa', marginBottom: 6 }}>Document: {selectedDoc?.label}</p>
              <p style={{ fontSize: 10, color: '#aaaaaa' }}>Patient: {patientName}</p>
            </div>
          )}

          {/* ━━━ STEP 3: DONE ━━━ */}
          {step === 3 && (
            <div style={{ textAlign: 'center', padding: '32px 0' }} data-testid="lens-step-done">
              <CheckCircle2 style={{ width: 40, height: 40, color: '#22c55e', margin: '0 auto 10px' }} />
              <p style={{ fontSize: 16, fontWeight: 500, color: '#111111', margin: '0 0 4px' }}>Added to chart</p>
              <p style={{ fontSize: 12, color: '#888888' }}>{selectedDoc?.label} → {patientName}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '0.5px solid #e8e8e8' }}>
          {step === 0 && (
            <button onClick={handleClose} style={{ width: '100%', height: 36, borderRadius: 8, background: '#ffffff', color: '#555555', fontSize: 12, border: '0.5px solid #e0e0e0', cursor: 'pointer' }}>Cancel</button>
          )}
          {step === 1 && (
            <button onClick={() => { reset(); }} style={{ width: '100%', height: 36, borderRadius: 8, background: '#ffffff', color: '#555555', fontSize: 12, border: '0.5px solid #e0e0e0', cursor: 'pointer' }}>Cancel scan</button>
          )}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button onClick={handleConfirm}
                style={{ width: '100%', height: 40, borderRadius: 8, background: '#111111', color: '#ffffff', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                data-testid="lens-confirm-btn"
              >
                Confirm and add to chart <ChevronRight style={{ width: 14, height: 14, opacity: 0.6 }} />
              </button>
              <button onClick={() => { setStep(0); setSelectedDoc(null); setScanResult(null); }}
                style={{ width: '100%', height: 32, borderRadius: 8, background: '#ffffff', color: '#555555', fontSize: 12, border: '0.5px solid #e0e0e0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
              >
                <ChevronLeft style={{ width: 12, height: 12 }} /> Scan a different document
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentUploadFlow;
